import { FC, useState } from 'react';

import { ArrowLeftOutlined, LockOutlined, PlusOutlined, QuestionOutlined } from '@ant-design/icons';
import { Button, Checkbox, Input, message, Modal, Tooltip } from 'antd';

import { MenuTab } from '../../../../../app/providers/redux/slices/app/types';
import {
  createTagRequest,
  reportsSliceActions,
} from '../../../../../app/providers/redux/slices/reports/params/reports/reportsSlice';
import { Tag } from '../../../../../app/providers/redux/slices/reports/params/reports/types';
import {
  useAppDispatch,
  useTypedSelector,
} from '../../../hooks/useTypedSelector';
import styles from '../styles/SideMenu.module.scss';
import { DatePeriodType, TabType } from '../types';
import { Tag as TagComponent } from './Tag';
import { ROLES } from '../../../../../helpers/utils/auth';

type SideMenuProps = {
    isOpen: boolean;
    onClose: () => void;
    activeMainTab: MenuTab;
    activeTab: TabType;
    setActiveTab: (tab: TabType) => void;
    onOpenHelpFrame: (url: string) => void;
    tags: {
        dashboards: Tag[];
        reports: Tag[];
    };
    collapsed: boolean
};

const reportDateTabs: Record<DatePeriodType, string> = {
  registrations: 'Регистрации',
  orders: 'Заказы',
  payments: 'Платежи',
  webinars: 'Вебинары',
  ads: 'Реклама',
};
const tabs = Object.keys(reportDateTabs) as DatePeriodType[];
const SuperPermission = new Set([ 'admin', 'superadmin', 'user:settings' ]);

const SideMenu: FC<SideMenuProps> = ({ isOpen, onClose, activeMainTab, activeTab, setActiveTab, onOpenHelpFrame, tags, collapsed }) => {
    const { selectedSchool } = useTypedSelector((state) => state.currentSchool);
    const currentUserRole = useTypedSelector(state => state.currentUser.role);
    const [ newTagName, setNewTagName ] = useState('');
    const [ isCreateTagModalOpen, setIsCreateTagModalOpen ] = useState(false);
    const [ isAllTag, setIsAllTag ] = useState(false);

    const dispatch = useAppDispatch();

    if (!isOpen) return null;

    const canAddTag = SuperPermission.has(currentUserRole);

    const currentTags = activeMainTab === 'reports'
        ? tags?.reports || []
        : tags?.dashboards || [];


    const allTags = [
        ...currentTags.map(tag => ({
            id: tag.id,
            name: tag.name,
            is_shared: tag.is_shared,
        }))
    ];

    const handleCreateTag = async () => {
        if (!selectedSchool?.id) {
            message.error('Школа не выбрана');
            return;
        }
        if (!newTagName.trim()) {
          message.error('Название тега не может быть пустым');
          return;
        }

        const kindId = activeMainTab === 'reports' ? 1 : 2;

        try {
            await dispatch(
                createTagRequest({
                    schoolId: selectedSchool.id,
                    kindId,
                    tagName: newTagName,
                    isAllTag
                })
            ).unwrap();

            message.success('Тег успешно создан');
            setIsCreateTagModalOpen(false);
            setNewTagName('');
        } catch (error) {
            message.error('Ошибка при создании тега');
        }
    };


    return (
        <div className={`${styles['side-menu']} ${collapsed ? styles['side-menu-collapsed'] : ''}`}>
            <div className={styles['side-menu-content']}>
              <Button className={styles['closed-button']} icon={<ArrowLeftOutlined/>} onClick={onClose}/>
                {activeMainTab === 'dashboards' && (
                    <div className={styles['menu-section']}>
                        <div
                            className={styles['menu-helper']}
                            onClick={() => onOpenHelpFrame('https://help.scananalytics.ru/dashbordy')}
                        >

                            <QuestionOutlined size={10}  style={{ background: 'red', color: 'white', borderRadius: '50%', padding: 5, fontSize: 12 }}/>
                            <span>Дашборды. Как работать?</span>
                        </div>
                    </div>
                )}
                {activeMainTab === 'reports' && (
                    <div className={styles['menu-section']}>
                        <div
                            className={styles['menu-helper']}
                            onClick={() => onOpenHelpFrame('https://help.scananalytics.ru/otchyoty/')}
                        >    
                            <QuestionOutlined size={10}  style={{ background: 'red', color: 'white', borderRadius: '50%', padding: 5, fontSize: 12 }}/>
                            <span>Отчёты. Как работать?</span>
                        </div>
                    </div>
                )}
                <div className={styles['menu-section']}>
                    <div
                        className={`${styles['menu-item']} ${activeTab === 'all' ? styles['active'] : ''}`}
                        onClick={() => setActiveTab('all')}
                    >
                        Все
                    </div>
                    <div
                        className={`${styles['menu-item']} ${activeTab === 'favorites' ? styles['active'] : ''}`}
                        onClick={() => setActiveTab('favorites')}
                    >
                        Избранные
                    </div>
                </div>
                {activeMainTab === 'reports' && (
                    <div className={styles['menu-section']}>
                        <div className={styles['menu-title']}>По дате периода:</div>
                        {tabs.map((tab) => (
                            <div
                                key={tab}
                                className={`${styles['menu-item']} ${activeTab === tab ? styles['active'] : ''}`}
                                onClick={() => {
                                    // Устанавливаем категорию и сбрасываем теги
                                    setActiveTab(tab);
                                    dispatch(reportsSliceActions.setActiveTag(tab));
                                  }}
                            >
                                {reportDateTabs[tab]}
                            </div>
                        ))}
                    </div>
                )}
                <div className={styles['menu-section']}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <div className={styles['menu-title']}>По тегам:</div>
                        <Tooltip title={!canAddTag ? 'Недостаточно прав для создания' : ''} trigger={'hover'} placement='right'>
                            <Button
                                style={{ border: 'none', marginRight: '15px' }}
                                icon={canAddTag ? <PlusOutlined style={{ color: 'grey' }} /> : <LockOutlined />}
                                size="small"
                                color="default"
                                onClick={() => setIsCreateTagModalOpen(true)}
                                disabled={!canAddTag}
                            />
                        </Tooltip>
                    </div>
                    {allTags?.map((tag) => 
                      <TagComponent
                        tag={tag}
                        activeTab={activeTab}
                        setActiveTab={setActiveTab}
                      />)
                    }
                </div>
            </div>
            <Modal
                title="Создать новый тег"
                open={isCreateTagModalOpen}
                onOk={handleCreateTag}
                onCancel={() => {
                    setIsCreateTagModalOpen(false);
                    setNewTagName('');
                }}
                okText="Создать"
                cancelText="Отмена"
            >
                {currentUserRole === ROLES.super && <span>
                    <Checkbox checked={isAllTag} onClick={() => setIsAllTag(p => !p)} id='allTag'/>
                    <label htmlFor='allTag'> Тег доступен всем ? </label>
                </span>}
                <Input
                    placeholder="Введите название тега"
                    value={newTagName}
                    onChange={(e) => setNewTagName(e.target.value)}
                />
            </Modal>
            <div className={styles['side-menu-overlay']} onClick={onClose} />
        </div>
    );
};

export default SideMenu;
